import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import type { FormInstance } from 'antd';

import { FILE_ATTACHMENT_MAX_COUNT, FILE_ATTACHMENT_MAX_SIZE_MB } from 'constants-module';
import { useNotification } from 'hooks';
import { Namespace } from 'enums';
import { Attachment as ApiAttachment } from 'types';

import { getOversizedAttachmentErrors } from '../../../../../helpers/attachment-validation';

export const useAttachmentHandlers = (
  form?: FormInstance,
  setAttachmentError?: (msg?: string) => void,
  setInvalidFileErrors?: (map: Record<string, string>) => void,
  setAttachmentCountError?: (msg?: string) => void,
) => {
  const { t } = useTranslation(Namespace.CompensationForm);
  const sendNotification = useNotification();

  const setFormError = useCallback(
    (message?: string) => {
      if (!form) return;

      form.setFields([
        {
          name: 'attachmentsCounter',
          errors: message ? [message] : [],
        },
      ]);
    },
    [form],
  );

  const handleAddAttachment = useCallback(
    (onChange: (value: any) => void, currentNewAttachments: File[], savedAttachments: (ApiAttachment | File)[]) =>
      (files: File[] | FileList | null) => {
        const incomingFilesArray: File[] = [];

        if (files) {
          if (Array.isArray(files)) {
            files.forEach((f) => {
              if (f && f instanceof File) incomingFilesArray.push(f);
            });
          } else {
            Array.from(files as FileList).forEach((f) => {
              if (f && f instanceof File) incomingFilesArray.push(f);
            });
          }
        }

        const filteredIncomingFiles: File[] = [];
        let duplicateFound = false;

        incomingFilesArray.forEach((incomingFile) => {
          const isDuplicate =
            currentNewAttachments.some((f) => f.name === incomingFile.name && f.size === incomingFile.size) ||
            filteredIncomingFiles.some((f) => f.name === incomingFile.name && f.size === incomingFile.size) ||
            (savedAttachments || []).some((a) => {
              const name = 'originalName' in a ? a.originalName : a.name;
              const size = 'size' in a ? a.size : undefined;

              return name === incomingFile.name && size === incomingFile.size;
            });

          if (isDuplicate) {
            duplicateFound = true;
          } else {
            filteredIncomingFiles.push(incomingFile);
          }
        });

        const newNewAttachments = [
          ...(currentNewAttachments || []).filter((f) => f instanceof File),
          ...filteredIncomingFiles,
        ];

        // total count = saved attachments (server-side) + new files
        const savedCount = Array.isArray(savedAttachments) ? savedAttachments.length : 0;
        const totalCount = savedCount + newNewAttachments.length;
        const countExceeded = totalCount > FILE_ATTACHMENT_MAX_COUNT;

        // determine oversized files among ALL new files
        const sizeMsg = t('attachmentSingleFileSizeError', { size: FILE_ATTACHMENT_MAX_SIZE_MB });
        const fileErrors = getOversizedAttachmentErrors(newNewAttachments, [], sizeMsg);

        if (setInvalidFileErrors) setInvalidFileErrors(fileErrors);

        if (duplicateFound) {
          sendNotification(t('attachmentDuplicateError'), 'warning');
        }

        if (countExceeded) {
          const countMsg = t('attachmentCountError', { count: FILE_ATTACHMENT_MAX_COUNT });

          if (setAttachmentCountError) setAttachmentCountError(countMsg);
          else if (form) setFormError(countMsg);
          else sendNotification(countMsg, 'warning');
        } else if (setAttachmentCountError) setAttachmentCountError(undefined);

        // update only new attachments state (File[])
        onChange(newNewAttachments);
      },
    [t, sendNotification, form, setInvalidFileErrors, setAttachmentCountError, setFormError],
  );

  const handleRemoveAttachment = useCallback(
    (
      onChange: (value: any) => void,
      targetAttachments: (File | ApiAttachment)[],
      otherAttachments: (File | ApiAttachment)[] = [],
    ) =>
      (fileName: string, attachmentId?: string, file?: File) => {
        const index = targetAttachments.findIndex((attachment) => {
          if (attachmentId && 'attachmentID' in attachment) {
            return attachment.attachmentID === attachmentId;
          }

          if (file && attachment instanceof File) {
            return attachment === file;
          }

          const name = 'originalName' in attachment ? attachment.originalName : attachment.name;

          return name === fileName;
        });

        if (index !== -1) {
          const newTarget = targetAttachments.slice();

          newTarget.splice(index, 1);
          onChange(newTarget);

          // Recompute total count using otherAttachments + newTarget
          const otherCount = Array.isArray(otherAttachments) ? otherAttachments.length : 0;
          const totalCount = otherCount + newTarget.length;
          const countExceeded = totalCount > FILE_ATTACHMENT_MAX_COUNT;

          if (countExceeded) {
            const countMsg = t('attachmentCountError', { count: FILE_ATTACHMENT_MAX_COUNT });

            if (setAttachmentCountError) setAttachmentCountError(countMsg);
            else if (form) setFormError(countMsg);
            else sendNotification(countMsg, 'warning');
          } else if (setAttachmentCountError) setAttachmentCountError(undefined);

          const sizeMsg = t('attachmentSingleFileSizeError', { size: FILE_ATTACHMENT_MAX_SIZE_MB });
          const fileErrors = getOversizedAttachmentErrors(newTarget, otherAttachments, sizeMsg);

          if (setInvalidFileErrors) setInvalidFileErrors(fileErrors);

          if (setAttachmentError && Object.keys(fileErrors).length === 0 && !countExceeded) {
            setAttachmentError(undefined);
          }
        }
      },
    [setAttachmentCountError, setInvalidFileErrors, setAttachmentError, t, form, setFormError, sendNotification],
  );

  return { handleAddAttachment, handleRemoveAttachment };
};
